import React, { useReducer, useMemo, useEffect, useCallback } from "react";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";

import {
  fetchTemplate,
  fetchPublicTemplate,
} from "../../api/actions/templates";
import { useAuthState } from "../../context/auth";
import { fetchContract } from "../../api/actions/contracts";
import { fetcher } from "../../api/client";
import { usePrevious } from "../../hooks/usePrevious";

import { editorReducer, initialEditorState } from "./reducer";

import { SET_DOCUMENT } from "./types";

const EditorStateContext = React.createContext();
const EditorDispatchContext = React.createContext();

function fetchAction(documentId, mode, hasAccount) {
  return mode === "public-template"
    ? fetchPublicTemplate(documentId, hasAccount)
    : mode === "template"
    ? fetchTemplate(documentId)
    : fetchContract(documentId);
}

function getDocument({ payload, mode, hasAccount }) {
  if (mode === "public-template" && hasAccount && payload?.data?.userTemplate)
    return payload.data.userTemplate;
  if (mode === "public-template") return payload.data.template;
  return payload.data;
}

function EditorProvider({ documentId, mode = "contract", children }) {
  const [state, dispatch] = useReducer(editorReducer, initialEditorState);
  const auth = useAuthState();
  const hasAccount = auth?.hasAccount;
  const prevHasAccount = usePrevious(hasAccount);

  const { status } = useQuery(
    [
      "document",
      documentId,
      {
        hasAccount,
      },
    ],
    () => fetcher(fetchAction(documentId, mode, hasAccount)),
    {
      onSuccess: (payload) => {
        console.log(payload);
        if (!state.document._id || prevHasAccount !== hasAccount) {
          const action = {
            type: SET_DOCUMENT,
            document: getDocument({ payload, mode, hasAccount }),
            mode,
            documentId,
            setOriginal: true,
          };
          if (mode === "public-template") {
            action.publicTemplate = payload.data;
          }
          dispatch(action);
        }
      },
    }
  );

  return (
    <EditorStateContext.Provider value={{ ...state, status, mode }}>
      <EditorDispatchContext.Provider value={dispatch}>
        {children}
      </EditorDispatchContext.Provider>
    </EditorStateContext.Provider>
  );
}

function useEditorState() {
  const context = React.useContext(EditorStateContext);
  if (context === undefined) {
    throw new Error("useEditorState must be used within a EditorProvider");
  }
  return context;
}

function useEditorDispatch() {
  const context = React.useContext(EditorDispatchContext);
  if (context === undefined) {
    throw new Error("useEditorDispatch must be used within a EditorProvider");
  }
  return context;
}

function useEditor() {
  return [useEditorState(), useEditorDispatch()];
}

export { EditorProvider, useEditor, useEditorState, useEditorDispatch };

export function getParticipantsMap(participants = []) {
  if (!participants.length) return new Map([]);

  return new Map(
    participants.map((participant, index) => {
      const draftParticipant = { ...participant, index: index + 1 };

      if (!draftParticipant.variables) {
        draftParticipant.variables = [];
      }

      draftParticipant.variables = new Map(
        draftParticipant.variables.map((variable) => {
          return [variable._id, variable];
        })
      );

      const signaturesByPageObj =
        draftParticipant?.signatures?.reduce((obj, signature) => {
          if (!obj[signature.page]) obj[signature.page] = [];
          obj[signature.page].push(signature);
          return obj;
        }, {}) ?? {};

      draftParticipant.signatures = new Map(
        draftParticipant.signatures?.map((signature) => {
          return [signature._id, signature];
        }) ?? []
      );

      draftParticipant.signaturesByPage = new Map(
        Object.keys(signaturesByPageObj).map((signaturePage) => {
          return [signaturePage, signaturesByPageObj[signaturePage]];
        })
      );

      return [participant._id, draftParticipant];
    })
  );
}
